import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Heart, PartyPopper } from "lucide-react";
import { useTranslation } from "../contexts/LocaleContext";

interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PaymentSuccessModal({
  isOpen,
  onClose,
}: PaymentSuccessModalProps) {
  const t = useTranslation();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 select-text"
          >
            {/* Top decorative gradient */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-400 via-indigo-500 to-purple-600" />

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all z-20"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="p-8 md:p-10 flex flex-col items-center text-center">
              {/* Animated Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  damping: 12,
                  stiffness: 200,
                  delay: 0.2,
                }}
                className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-3xl flex items-center justify-center mb-6 text-emerald-600 dark:text-emerald-400 shadow-inner relative"
              >
                <CheckCircle2 size={40} strokeWidth={2.5} />
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 bg-emerald-400/20 rounded-3xl"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-2xl md:text-4xl font-black text-slate-900 dark:text-white mb-3 tracking-tight flex items-center justify-center gap-2">
                  {t.userSettings.paymentSuccess.title}{" "}
                </h2>
                <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed max-w-xs mx-auto font-medium">
                  {t.userSettings.paymentSuccess.subtitle}{" "}
                </p>
              </motion.div>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-8 w-full py-4 px-6 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-700 text-white font-black text-sm shadow-xl shadow-indigo-500/25 hover:from-indigo-500 hover:to-purple-600 transition-all duration-300"
              >
                {t.userSettings.paymentSuccess.button}
              </motion.button>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex items-center justify-center gap-2 mt-6 text-slate-400 dark:text-slate-500 italic text-xs"
              >
                <Heart size={14} className="text-rose-500 fill-rose-500" />
                <span>{t.userSettings.paymentSuccess.team}</span>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
